import React, { useState, useEffect } from "react";
import { X, Pencil } from "lucide-react";

interface RegularShift {
  day: string;
  dayIndex: number;
  isWorking: boolean;
  startTime: string;
  endTime: string;
  scheduleId?: number;
}

interface EmployeeRegularShiftsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (shifts: RegularShift[]) => void;
  employeeName: string;
  existingShifts?: RegularShift[];
}

const EmployeeRegularShiftsModal: React.FC<EmployeeRegularShiftsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  employeeName,
  existingShifts,
}) => {
  const daysOfWeek = [
    { name: "Monday", index: 1 },
    { name: "Tuesday", index: 2 },
    { name: "Wednesday", index: 3 },
    { name: "Thursday", index: 4 },
    { name: "Friday", index: 5 },
    { name: "Saturday", index: 6 },
    { name: "Sunday", index: 0 },
  ];

  const [shifts, setShifts] = useState<RegularShift[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Purple theme colors
  const primaryPurple = "#885ABB";
  const darkPurple = "#402B69";
  const lightPurple = "#C4A6E1";
  const veryLightPurple = "#F1ECFB";

  // Initialize shifts
  useEffect(() => {
    if (isOpen) {
      if (existingShifts && existingShifts.length > 0) {
        setShifts(existingShifts.map((shift) => ({ ...shift })));
      } else {
        // Default: all days enabled with 9:00 AM - 6:00 PM
        setShifts(
          daysOfWeek.map((day) => ({
            day: day.name,
            dayIndex: day.index,
            isWorking: true,
            startTime: "09:00",
            endTime: "18:00",
            scheduleId: undefined,
          }))
        );
      }
      setEditingIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingShifts]);

  // Toggle working status for a day
  const handleToggleWorking = (index: number) => {
    setShifts(
      shifts.map((shift, i) => {
        if (i !== index) return shift;
        const isWorking = !shift.isWorking;
        return {
          ...shift,
          isWorking,
          startTime: isWorking
            ? shift.startTime || "09:00"
            : "",
          endTime: isWorking
            ? shift.endTime || "18:00"
            : "",
        };
      })
    );
  };

  // Handle time change
  const handleTimeChange = (
    index: number,
    field: "startTime" | "endTime",
    value: string
  ) => {
    setShifts(
      shifts.map((shift, i) =>
        i === index ? { ...shift, [field]: value } : shift
      )
    );
  };

  // Format time for display (24h to 12h)
  const formatTimeDisplay = (time24: string): string => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    const hour12 = hour % 12 || 12;
    return `${hour12.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  // Handle edit click
  const handleEditClick = (index: number) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    } else {
      setEditingIndex(index);
    }
  };

  // Handle save
  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(shifts);
      onClose();
    } catch (error) {
      console.error("Error saving regular shifts:", error);
      alert("Failed to save regular shifts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: lightPurple }}>
          <h2 className="text-lg font-semibold" style={{ color: darkPurple }}>
            Employee Regular Shifts
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" style={{ color: primaryPurple }} strokeWidth={2.5} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ backgroundColor: primaryPurple }}>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-white border-r border-white/20">
                      DAY
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-white border-r border-white/20">
                      IS WORKING
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-white border-r border-white/20">
                      START DATE TIME
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-white border-r border-white/20">
                      END DATE TIME
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-white">
                      ACTION
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift, index) => (
                    <tr
                      key={index}
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      {/* Day */}
                      <td className="px-4 py-4 text-sm font-medium" style={{ color: darkPurple }}>
                        {shift.day}
                      </td>

                      {/* IS WORKING Toggle */}
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => handleToggleWorking(index)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            shift.isWorking
                              ? "focus:ring-purple-500"
                              : "focus:ring-gray-400"
                          }`}
                          style={{
                            backgroundColor: shift.isWorking
                              ? primaryPurple
                              : "#D1D5DB",
                          }}
                          aria-label={`Toggle ${shift.day} working status`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              shift.isWorking ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>

                      {/* START DATE TIME */}
                      <td className="px-4 py-4 text-center">
                        {editingIndex === index ? (
                          <input
                            type="time"
                            value={shift.startTime}
                            onChange={(e) =>
                              handleTimeChange(index, "startTime", e.target.value)
                            }
                            className="px-2 py-1 border rounded text-sm focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                            style={{
                              borderColor: lightPurple,
                            }}
                            onFocus={(e) => {
                              if (shift.isWorking) {
                                e.target.style.borderColor = primaryPurple;
                                e.target.style.boxShadow = `0 0 0 2px ${veryLightPurple}`;
                              }
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = lightPurple;
                              e.target.style.boxShadow = "none";
                            }}
                            disabled={!shift.isWorking}
                          />
                        ) : (
                          <span className="text-sm" style={{ color: shift.isWorking ? darkPurple : "#9CA3AF" }}>
                            {shift.isWorking ? formatTimeDisplay(shift.startTime) : "-"}
                          </span>
                        )}
                      </td>

                      {/* END DATE TIME */}
                      <td className="px-4 py-4 text-center">
                        {editingIndex === index ? (
                          <input
                            type="time"
                            value={shift.endTime}
                            onChange={(e) =>
                              handleTimeChange(index, "endTime", e.target.value)
                            }
                            className="px-2 py-1 border rounded text-sm focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                            style={{
                              borderColor: lightPurple,
                            }}
                            onFocus={(e) => {
                              if (shift.isWorking) {
                                e.target.style.borderColor = primaryPurple;
                                e.target.style.boxShadow = `0 0 0 2px ${veryLightPurple}`;
                              }
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = lightPurple;
                              e.target.style.boxShadow = "none";
                            }}
                            disabled={!shift.isWorking}
                          />
                        ) : (
                          <span className="text-sm" style={{ color: shift.isWorking ? darkPurple : "#9CA3AF" }}>
                            {shift.isWorking ? formatTimeDisplay(shift.endTime) : "-"}
                          </span>
                        )}
                      </td>

                      {/* ACTION */}
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => handleEditClick(index)}
                          className="p-2 rounded hover:opacity-80 transition-opacity"
                          style={{
                            backgroundColor: primaryPurple,
                          }}
                          aria-label={`Edit ${shift.day} shift`}
                        >
                          <Pencil className="w-4 h-4 text-white" strokeWidth={2.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0" style={{ borderColor: lightPurple }}>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-md text-white font-medium transition-colors disabled:opacity-50 hover:opacity-90"
            style={{ backgroundColor: "#EF4444" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 rounded-md text-white font-medium transition-colors disabled:opacity-50 hover:opacity-90"
            style={{ backgroundColor: primaryPurple }}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeRegularShiftsModal;
